import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  alpha,
} from '@mui/material';
import {
  Share,
  ArrowBack,
  People,
  FilterList,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { AppLayout } from '@/components/layout';
import { TodoItem, TaskInput } from '@/components/todos';
import { TodoShareDialog } from '@/components/dialogs';
import { useTodoList } from '@/hooks';
import { brandColors } from '@/theme/colors';
import type { TaskPriority } from '@/types/todolist';
import type { RecurrenceRule } from '@/types/recurrence';
import { getCompletionPercentage } from '@/types/todolist';

type FilterType = 'all' | 'active' | 'completed';

export const TodoListPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    todoList,
    loading,
    error,
    canEdit,
    addItem,
    updateItem,
    toggleItem,
    deleteItem,
    reorderItems,
    addSubtask,
    toggleSubtask,
    deleteSubtask,
  } = useTodoList(id || '');

  const [shareOpen, setShareOpen] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && todoList) {
      const oldIndex = todoList.items.findIndex((item) => item.id === active.id);
      const newIndex = todoList.items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = [...todoList.items];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);

        const itemIds = newItems.map((item) => item.id);
        reorderItems(itemIds);
      }
    }
  };

  const handleAddTask = (
    title: string,
    dueDate: Date | null,
    priority: TaskPriority | null,
    recurrence: RecurrenceRule | null
  ) => {
    addItem(title, dueDate, priority, recurrence);
  };

  const filteredItems = useMemo(() => {
    if (!todoList) return [];
    switch (filter) {
      case 'active':
        return todoList.items.filter((item) => !item.completed);
      case 'completed':
        return todoList.items.filter((item) => item.completed);
      default:
        return todoList.items;
    }
  }, [todoList, filter]);

  const completionPercentage = todoList ? getCompletionPercentage(todoList) : 0;
  const completedCount = todoList?.items.filter((i) => i.completed).length || 0;
  const totalCount = todoList?.items.length || 0;

  if (loading) {
    return (
      <AppLayout showBackToDashboard title="Loading...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error || !todoList) {
    return (
      <AppLayout showBackToDashboard backToSection="todolists" title="Error">
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.message || 'ToDo list not found'}
          </Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard', { state: { section: 'todolists' } })}>
            Back to Dashboard
          </Button>
        </Container>
      </AppLayout>
    );
  }

  return (
    <AppLayout showBackToDashboard backToSection="todolists" title={todoList.name}>
      <Container
        maxWidth="md"
        sx={{
          py: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)', // Account for AppLayout header
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: brandColors.purple }}>
                {todoList.name}
              </Typography>
              {todoList.description && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  {todoList.description}
                </Typography>
              )}
            </Box>
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={() => setShareOpen(true)}
                sx={{
                  borderColor: brandColors.purple,
                  color: brandColors.purple,
                  '&:hover': {
                    borderColor: brandColors.purpleDeep,
                    bgcolor: alpha(brandColors.purple, 0.05),
                  },
                }}
              >
                Share
              </Button>
            )}
          </Box>

          {/* Progress bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                flexGrow: 1,
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(brandColors.purple, 0.1),
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${completionPercentage}%`,
                  height: '100%',
                  borderRadius: 4,
                  bgcolor: completionPercentage === 100 ? brandColors.accent.green : brandColors.purple,
                  transition: 'width 0.3s ease-in-out',
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
              {completedCount}/{totalCount} done
            </Typography>
          </Box>

          {/* Shared indicator */}
          {todoList.sharedWith.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Chip
                icon={<People sx={{ fontSize: 16 }} />}
                label={`Shared with ${todoList.sharedWith.length}`}
                size="small"
                sx={{
                  bgcolor: alpha(brandColors.purple, 0.1),
                  color: brandColors.purple,
                }}
              />
              {!canEdit && (
                <Chip
                  label="View Only"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </Box>

        {/* Add new item */}
        {canEdit && (
          <Box sx={{ mb: 3, flexShrink: 0 }}>
            <TaskInput onAdd={handleAddTask} />
          </Box>
        )}

        {/* Scrollable content area */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            mr: -2,
            pr: 2,
          }}
        >
          {/* Filter bar */}
          {todoList.items.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <FilterList />
                </IconButton>
                {(['all', 'active', 'completed'] as FilterType[]).map((f) => (
                  <Chip
                    key={f}
                    label={f.charAt(0).toUpperCase() + f.slice(1)}
                    size="small"
                    variant={filter === f ? 'filled' : 'outlined'}
                    onClick={() => setFilter(f)}
                    sx={{
                      ...(filter === f && {
                        bgcolor: brandColors.purple,
                        color: 'white',
                      }),
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {filteredItems.length} {filter === 'all' ? 'tasks' : filter}
              </Typography>
            </Box>
          )}

          {/* Todo items */}
          {filteredItems.length === 0 ? (
            <Paper
              sx={{
                textAlign: 'center',
                py: 6,
                bgcolor: alpha(brandColors.purple, 0.02),
                border: '1px dashed',
                borderColor: alpha(brandColors.purple, 0.2),
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {filter === 'all' ? 'No tasks yet' : `No ${filter} tasks`}
              </Typography>
              {filter === 'all' && canEdit && (
                <Typography variant="body2" color="text.secondary">
                  Add your first task above to get started
                </Typography>
              )}
            </Paper>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredItems.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <Box sx={{ pb: 2 }}>
                  {filteredItems.map((item) => (
                    <TodoItem
                      key={item.id}
                      item={item}
                      canEdit={canEdit}
                      onToggle={toggleItem}
                      onUpdate={updateItem}
                      onDelete={deleteItem}
                      onAddSubtask={addSubtask}
                      onToggleSubtask={toggleSubtask}
                      onDeleteSubtask={deleteSubtask}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Container>

      {/* Share Dialog */}
      {todoList && (
        <TodoShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          todoList={todoList}
        />
      )}
    </AppLayout>
  );
};
